/**
 * Bollinger Bands Quantum — constantes y resolución de config.
 *
 * SAFE_DEFAULTS es la única fuente de verdad de defaults; todo consumo de
 * config pasa por resolveConfig() (patrón E.2 del Standard) para que ninguna
 * clave llegue undefined aunque la config persistida sea parcial o corrupta.
 *
 * Los colores se guardan como `null` (= usar paleta Quantum); resolveColor /
 * resolveColors los materializan en el momento de pintar o de mostrar el
 * picker, sin persistir el hex salvo que el usuario lo cambie.
 */

export const QUANTUM_COLORS = Object.freeze({
  bull: '#1B5E20',
  bear: '#B71C1C',
  neutral: '#374151',
  squeeze: '#d4af37',
})

export const SAFE_DEFAULTS = Object.freeze({
  _version: 1,

  // ── Activación (la puerta real es activeRegistry; esto es un apagado suave
  // desde el panel: detect/render devuelven vacío con enabled === false) ────
  enabled: true,

  // ── Cálculo ──────────────────────────────────────────────────────────────
  period: 20,
  multiplier: 2.0,
  priceType: 'close', // 'close' | 'hl2' | 'hlc3' | 'ohlc4'
  squeezeThreshold: 5.0, // bandwidth (%) por debajo del cual hay squeeze

  // ── Visualización ────────────────────────────────────────────────────────
  showUpper: true,
  showMiddle: true,
  showLower: true,
  showFill: true,
  showLabels: true,
  showSqueeze: true,

  // ── Colores (null = paleta Quantum) ──────────────────────────────────────
  // upper: bear · lower: bull · middle: neutral · fill: bull · squeeze: dorado
  upperColor: null,
  lowerColor: null,
  middleColor: null,
  fillColor: null,
  squeezeColor: null,

  // ── Estilo ───────────────────────────────────────────────────────────────
  lineWidth: 1.5, // 1 | 1.5 | 2
  middleLineStyle: 'solid', // 'solid' | 'dotted'

  // ── Dashboard / panel ────────────────────────────────────────────────────
  showDashboard: true,
  dashboardMinimized: false,
  badgePosition: { x: 8, y: 8 },
  panelSections: {},
})

export const PERIOD_RANGE = Object.freeze({ min: 5, max: 200 })
export const MULTIPLIER_RANGE = Object.freeze({ min: 0.5, max: 5.0 })
export const SQUEEZE_RANGE = Object.freeze({ min: 1.0, max: 20.0 })
export const PRICE_TYPES = Object.freeze(['close', 'hl2', 'hlc3', 'ohlc4'])
export const LINE_WIDTHS = Object.freeze([1, 1.5, 2])
export const MIDDLE_LINE_STYLES = Object.freeze(['solid', 'dotted'])

export const LABEL_FONT = '10px -apple-system, BlinkMacSystemFont, "Trebuchet MS", Roboto, Ubuntu, sans-serif'

// ── Opacidad del relleno modulada por bandwidth ─────────────────────────────
// El relleno es más opaco con bandas anchas y más tenue en squeeze:
// alpha = FILL_ALPHA_MIN + t * (FILL_ALPHA_MAX - FILL_ALPHA_MIN), con
// t = clamp(bandwidth / (4 * squeezeThreshold), 0, 1). Es decir, un bandwidth
// de 4× el umbral de squeeze ya se considera "bandas totalmente abiertas".
export const FILL_ALPHA_MIN = 0.04
export const FILL_ALPHA_MAX = 0.14

export function fillAlphaForBandwidth(bandwidth, squeezeThreshold) {
  const bw = Number.isFinite(bandwidth) ? bandwidth : 0
  const th = Number.isFinite(squeezeThreshold) && squeezeThreshold > 0 ? squeezeThreshold : SAFE_DEFAULTS.squeezeThreshold
  const t = Math.min(1, Math.max(0, bw / (4 * th)))
  return FILL_ALPHA_MIN + t * (FILL_ALPHA_MAX - FILL_ALPHA_MIN)
}

// ── Helpers internos de saneado ─────────────────────────────────────────────

function clampNumber(v, min, max, fallback) {
  const n = typeof v === 'string' ? Number(v) : v
  if (!Number.isFinite(n)) return fallback
  return Math.min(max, Math.max(min, n))
}

function asBool(v, fallback) {
  return v == null ? fallback : Boolean(v)
}

function asEnum(v, allowed, fallback) {
  return allowed.includes(v) ? v : fallback
}

function asColorOrNull(v) {
  return typeof v === 'string' && v.length > 0 ? v : null
}

/**
 * Merge defensivo: defaults + config persistida + saneado por clave.
 * Nunca devuelve undefined en ninguna clave conocida. Si `raw` no es un
 * objeto, devuelve una copia de SAFE_DEFAULTS.
 */
export function resolveConfig(raw) {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ...SAFE_DEFAULTS }
  }
  const merged = { ...SAFE_DEFAULTS, ...raw }
  return {
    ...merged,
    enabled: asBool(merged.enabled, true),
    period: Math.round(clampNumber(merged.period, PERIOD_RANGE.min, PERIOD_RANGE.max, SAFE_DEFAULTS.period)),
    multiplier: clampNumber(merged.multiplier, MULTIPLIER_RANGE.min, MULTIPLIER_RANGE.max, SAFE_DEFAULTS.multiplier),
    priceType: asEnum(merged.priceType, PRICE_TYPES, SAFE_DEFAULTS.priceType),
    squeezeThreshold: clampNumber(merged.squeezeThreshold, SQUEEZE_RANGE.min, SQUEEZE_RANGE.max, SAFE_DEFAULTS.squeezeThreshold),
    showUpper: asBool(merged.showUpper, true),
    showMiddle: asBool(merged.showMiddle, true),
    showLower: asBool(merged.showLower, true),
    showFill: asBool(merged.showFill, true),
    showLabels: asBool(merged.showLabels, true),
    showSqueeze: asBool(merged.showSqueeze, true),
    upperColor: asColorOrNull(merged.upperColor),
    lowerColor: asColorOrNull(merged.lowerColor),
    middleColor: asColorOrNull(merged.middleColor),
    fillColor: asColorOrNull(merged.fillColor),
    squeezeColor: asColorOrNull(merged.squeezeColor),
    lineWidth: LINE_WIDTHS.includes(merged.lineWidth) ? merged.lineWidth : SAFE_DEFAULTS.lineWidth,
    middleLineStyle: asEnum(merged.middleLineStyle, MIDDLE_LINE_STYLES, SAFE_DEFAULTS.middleLineStyle),
    showDashboard: asBool(merged.showDashboard, true),
    dashboardMinimized: asBool(merged.dashboardMinimized, false),
    badgePosition: merged.badgePosition && Number.isFinite(merged.badgePosition.x)
      ? merged.badgePosition
      : { x: 8, y: 8 },
    panelSections: merged.panelSections && typeof merged.panelSections === 'object' ? merged.panelSections : {},
  }
}

/** null → color de paleta; string no vacío → tal cual. */
export function resolveColor(value, fallback) {
  return typeof value === 'string' && value.length > 0 ? value : fallback
}

/** Materializa los 5 colores del indicador a partir de una config resuelta. */
export function resolveColors(cfg) {
  return {
    upper: resolveColor(cfg.upperColor, QUANTUM_COLORS.bear),
    lower: resolveColor(cfg.lowerColor, QUANTUM_COLORS.bull),
    middle: resolveColor(cfg.middleColor, QUANTUM_COLORS.neutral),
    fill: resolveColor(cfg.fillColor, QUANTUM_COLORS.bull),
    squeeze: resolveColor(cfg.squeezeColor, QUANTUM_COLORS.squeeze),
  }
}

/** hex '#RRGGBB' o 'rgb(...)' → 'rgba(r, g, b, alpha)'. */
export function withAlpha(color, alpha) {
  const a = Math.min(1, Math.max(0, alpha))
  if (typeof color !== 'string' || color.length === 0) return `rgba(0,0,0,${a})`
  if (color.startsWith('rgba(')) {
    return color.replace(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/, `rgba($1, $2, $3, ${a})`)
  }
  if (color.startsWith('rgb(')) {
    const rgb = color.match(/\d+/g)
    return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${a})`
  }
  if (color.startsWith('#')) {
    const hex = color.slice(1)
    const n = parseInt(hex, 16)
    if (Number.isNaN(n) || hex.length < 6) return color
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
  }
  return color
}
